import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getAdminUsers, AdminUser } from "services/admin/adminUsersService";
import Spinner from "components/common/Spinner";
import { useTheme } from "contexts/ThemeContext";
import { AlertTriangle, Search, User, CheckCircle2, ArrowUp, ArrowDown, X, XCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { logger } from 'utils/logger';

export function UserTable() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [subscriptionTypeFilter, setSubscriptionTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<'created_on' | 'email' | 'subscriptionEndDate'>('created_on');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Theme tokens
  const tableHeaderBg = isDark ? '#1D293D' : '#F8FAFC';
  const tableBorderColor = isDark ? '#314158' : '#E2E8F0';
  const tableRowHoverBg = isDark ? '#162030' : '#F8FAFC';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';
  const textSecondary = isDark ? '#90A1B9' : '#64748B';
  const inputBg = isDark ? '#1D293D' : '#FFFFFF';
  const inputBorder = isDark ? '#314158' : '#D1D5DB';
  const inputText = isDark ? '#E2E8F0' : '#374151';
  const errorBg = isDark ? '#3B1010' : '#FEF2F2';
  const errorText = isDark ? '#F87171' : '#DC2626';
  const errorBorder = isDark ? '#7F1D1D' : '#EF4444';
  const avatarBg = isDark ? '#314158' : '#E2E8F0';
  const avatarIconColor = isDark ? '#90A1B9' : '#64748B';
  const activeColor = isDark ? '#5EE9B5' : '#16A34A';
  const inactiveColor = isDark ? '#5A6E83' : '#9CA3AF';
  const verifiedColor = isDark ? '#5EE9B5' : '#16A34A';
  const paginationBorder = isDark ? '#314158' : '#E2E8F0';
  const paginationText = isDark ? '#90A1B9' : '#374151';
  const mutedIcon = isDark ? '#5A6E83' : '#9CA3AF';

  const inputStyle: React.CSSProperties = {
    background: inputBg,
    border: `1px solid ${inputBorder}`,
    color: inputText,
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await getAdminUsers({
          page: currentPage,
          limit: itemsPerPage,
          search: searchQuery || undefined,
          subscriptionType: subscriptionTypeFilter || undefined,
          status: statusFilter || undefined,
          sortBy: sortBy,
          sortOrder: sortOrder
        });
        setUsers(response.users || []);
        setTotalPages(response.pagination?.totalPages || 1);
        setTotalItems(response.pagination?.total || 0);
        setError(null);
      } catch (err: unknown) {
        setError(t('admin.errors.failedToFetchUsers', 'Failed to fetch users'));
        setUsers([]);
        logger.error("Error fetching users:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [currentPage, itemsPerPage, searchQuery, subscriptionTypeFilter, statusFilter, sortBy, sortOrder, t]);

  // Format date for display in DD/MM/YY
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const getSubscriptionTypeBadgeStyle = (type: string | null): React.CSSProperties => {
    switch (type) {
      case "Trial":
        return { background: isDark ? '#2D1B4E' : '#F3E8FF', color: isDark ? '#C084FC' : '#7C3AED', borderColor: isDark ? '#581C87' : '#DDD6FE' };
      case "Starter":
        return { background: isDark ? '#162456' : '#DBEAFE', color: isDark ? '#8EC5FF' : '#1D4ED8', borderColor: isDark ? '#1C398E' : '#93C5FD' };
      default:
        return { background: isDark ? '#1D293D' : '#F1F5F9', color: isDark ? '#90A1B9' : '#475569', borderColor: isDark ? '#314158' : '#E2E8F0' };
    }
  };

  const getStatusBadgeStyle = (status: string | null): React.CSSProperties => {
    switch (status) {
      case "active":
        return { background: isDark ? '#022C22' : '#DCFCE7', color: isDark ? '#5EE9B5' : '#15803D', borderColor: isDark ? '#065F46' : '#BBF7D0' };
      case "trial":
        return { background: isDark ? '#2D1B4E' : '#F3E8FF', color: isDark ? '#C084FC' : '#7C3AED', borderColor: isDark ? '#581C87' : '#DDD6FE' };
      case "expired":
        return { background: isDark ? '#3B1010' : '#FEE2E2', color: isDark ? '#F87171' : '#DC2626', borderColor: isDark ? '#7F1D1D' : '#FECACA' };
      case "cancelled":
        return { background: isDark ? '#1D293D' : '#F1F5F9', color: isDark ? '#90A1B9' : '#475569', borderColor: isDark ? '#314158' : '#E2E8F0' };
      default:
        return { background: isDark ? '#1D293D' : '#F1F5F9', color: isDark ? '#90A1B9' : '#475569', borderColor: isDark ? '#314158' : '#E2E8F0' };
    }
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Handle search
  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  // Handle filter changes
  const handleSubscriptionTypeChange = (value: string) => {
    setSubscriptionTypeFilter(value);
    setCurrentPage(1);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setSubscriptionTypeFilter('');
    setStatusFilter('');
    setSortBy('created_on');
    setSortOrder('desc');
    setCurrentPage(1);
  };

  // Generate pagination items
  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      items.push(
        <button
          key="first"
          onClick={() => handlePageChange(1)}
          className="px-3 py-1.5 text-xs rounded-md transition-colors"
          style={{ border: `1px solid ${paginationBorder}`, color: paginationText }}
        >
          1
        </button>
      );

      if (startPage > 2) {
        items.push(
          <span key="ellipsis-start" className="px-2" style={{ color: mutedIcon }}>...</span>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className="px-3 py-1.5 text-xs rounded-md transition-colors"
          style={i === currentPage
            ? { background: '#007bff', color: '#FFFFFF', border: '1px solid #007bff' }
            : { border: `1px solid ${paginationBorder}`, color: paginationText }
          }
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(
          <span key="ellipsis-end" className="px-2" style={{ color: mutedIcon }}>...</span>
        );
      }

      items.push(
        <button
          key="last"
          onClick={() => handlePageChange(totalPages)}
          className="px-3 py-1.5 text-xs rounded-md transition-colors"
          style={{ border: `1px solid ${paginationBorder}`, color: paginationText }}
        >
          {totalPages}
        </button>
      );
    }

    return items;
  };

  if (error) {
    return (
      <div className="p-4 rounded" style={{ background: errorBg, borderLeft: `4px solid ${errorBorder}` }}>
        <div className="flex items-start gap-2">
          <AlertTriangle size={14} style={{ color: errorText, marginTop: 2 }} />
          <p className="text-xs" style={{ color: errorText }}>{error}</p>
        </div>
      </div>
    );
  }

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="medium" text={t('admin.loading.users', 'Loading users...')} />
      </div>
    );
  }

  const hasActiveFilters = searchQuery || subscriptionTypeFilter || statusFilter;

  return (
    <div className="p-5 space-y-4">
      {/* Search and Filters */}
      <div className="space-y-3">
        <div className="flex flex-col lg:flex-row gap-2 items-stretch lg:items-center">
          {/* Search Input */}
          <div className="lg:flex-[2] min-w-0">
            <div className="relative">
              <input
                type="text"
                placeholder={t('admin.users.searchPlaceholder', 'Search by name or email...')}
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                style={inputStyle}
              />
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: mutedIcon }} />
            </div>
          </div>

          {/* Subscription Type Filter */}
          <select
            value={subscriptionTypeFilter}
            onChange={(e) => handleSubscriptionTypeChange(e.target.value)}
            className="lg:w-36 px-2 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={inputStyle}
          >
            <option value="">All Subscriptions</option>
            <option value="Trial">Trial</option>
            <option value="Starter">Starter</option>
          </select>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => handleStatusChange(e.target.value)}
            className="lg:w-32 px-2 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={inputStyle}
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="trial">Trial</option>
            <option value="expired">Expired</option>
            <option value="cancelled">Cancelled</option>
          </select>

          {/* Sort By */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'created_on' | 'email' | 'subscriptionEndDate')}
            className="lg:w-36 px-2 py-2 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={inputStyle}
          >
            <option value="created_on">Join Date</option>
            <option value="email">Email</option>
            <option value="subscriptionEndDate">Expiration</option>
          </select>

          {/* Sort Order */}
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="lg:w-20 px-2 py-2 text-xs rounded-md transition-colors flex items-center gap-1 whitespace-nowrap justify-center"
            style={{ border: `1px solid ${inputBorder}`, color: inputText }}
            title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
          >
            {sortOrder === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
            {sortOrder === 'asc' ? 'A-Z' : 'Z-A'}
          </button>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="lg:w-24 px-2 py-2 text-xs rounded-md transition-colors flex items-center gap-1 whitespace-nowrap justify-center"
              style={{ border: `1px solid ${isDark ? '#7F1D1D' : '#FCA5A5'}`, color: errorText }}
            >
              <XCircle size={12} />
              Clear
            </button>
          )}
        </div>

        {/* Active Filters Info */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 items-center text-xs" style={{ color: subtitleColor }}>
            <span className="font-medium">{t('admin.users.activeFilters', 'Active filters')}:</span>
            {searchQuery && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: isDark ? '#162456' : '#DBEAFE', color: isDark ? '#8EC5FF' : '#1D4ED8' }}>
                Search: "{searchQuery}"
                <button onClick={() => setSearchQuery('')}><X size={10} /></button>
              </span>
            )}
            {subscriptionTypeFilter && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: isDark ? '#2D1B4E' : '#F3E8FF', color: isDark ? '#C084FC' : '#7C3AED' }}>
                Type: {subscriptionTypeFilter}
                <button onClick={() => setSubscriptionTypeFilter('')}><X size={10} /></button>
              </span>
            )}
            {statusFilter && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md" style={{ background: isDark ? '#022C22' : '#DCFCE7', color: isDark ? '#5EE9B5' : '#15803D' }}>
                Status: {statusFilter}
                <button onClick={() => setStatusFilter('')}><X size={10} /></button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Table Controls */}
      <div className="flex justify-between items-center">
        <div className="text-xs" style={{ color: subtitleColor }}>
          Showing {users.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} users
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs" style={{ color: subtitleColor }}>{t('admin.users.rowsPerPage', 'Rows per page')}:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(parseInt(e.target.value));
              setCurrentPage(1);
            }}
            disabled={loading}
            className="px-2 py-1 text-xs rounded-md focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300 disabled:opacity-50"
            style={inputStyle}
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg" style={{ border: `1px solid ${tableBorderColor}` }}>
        <table className="w-full">
          <thead style={{ background: tableHeaderBg, borderBottom: `1px solid ${tableBorderColor}` }}>
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: subtitleColor }}>
                {t('admin.table.name', 'Name')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: subtitleColor }}>
                {t('admin.table.email', 'Email')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: subtitleColor }}>
                {t('admin.table.subscriptionType', 'Subscription Type')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: subtitleColor }}>
                {t('admin.table.status', 'Status')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: subtitleColor }}>
                {t('admin.table.subscriptionEnd', 'Subscription End')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: subtitleColor }}>
                {t('admin.table.accounts', 'Accounts')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: subtitleColor }}>
                {t('admin.table.lastPayment', 'Last Payment')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: subtitleColor }}>
                {t('admin.table.joinDate', 'Joined')}
              </th>
            </tr>
          </thead>
          <tbody style={{ background: 'transparent' }}>
            {users && users.length > 0 ? users.map((user) => (
              <tr
                key={user._id}
                onMouseEnter={() => setHoveredRow(user._id)}
                onMouseLeave={() => setHoveredRow(null)}
                style={{
                  background: hoveredRow === user._id ? tableRowHoverBg : 'transparent',
                  borderBottom: `1px solid ${tableBorderColor}`,
                  transition: 'background-color 0.15s'
                }}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {user.profilePicture ? (
                      <img
                        src={user.profilePicture}
                        alt={user.name}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: avatarBg }}>
                        <User size={16} color={avatarIconColor} />
                      </div>
                    )}
                    <div>
                      <div className="text-xs font-medium" style={{ color: titleColor }}>{user.name}</div>
                      {user.isActive ? (
                        <span className="text-xs flex items-center gap-1" style={{ color: activeColor }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: activeColor, display: 'inline-block' }} />
                          Active
                        </span>
                      ) : (
                        <span className="text-xs flex items-center gap-1" style={{ color: inactiveColor }}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', background: inactiveColor, display: 'inline-block' }} />
                          Inactive
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-xs" style={{ color: titleColor }}>{user.email}</div>
                  {user.isEmailVerified && (
                    <span className="text-xs flex items-center gap-0.5" style={{ color: verifiedColor }}>
                      <CheckCircle2 size={12} style={{ marginRight: 2 }} />Verified
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span
                    className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium"
                    style={{ ...getSubscriptionTypeBadgeStyle(user.subscription?.type), border: `1px solid ${getSubscriptionTypeBadgeStyle(user.subscription?.type).borderColor}` }}
                  >
                    {user.subscription?.type || 'None'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span
                    className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium"
                    style={{ ...getStatusBadgeStyle(user.subscription?.status), border: `1px solid ${getStatusBadgeStyle(user.subscription?.status).borderColor}` }}
                  >
                    {user.subscription?.status || 'none'}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs" style={{ color: subtitleColor }}>
                  {formatDate(user.subscription?.endDate)}
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs font-semibold" style={{ color: titleColor }}>{user.accountsCount || 0}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="text-xs font-medium" style={{ color: titleColor }}>
                    ${(user.payment?.lastAmount || 0).toFixed(2)}
                  </div>
                  <div className="text-xs" style={{ color: textSecondary }}>{formatDate(user.payment?.lastPaymentDate)}</div>
                </td>
                <td className="px-4 py-3 text-xs" style={{ color: subtitleColor }}>
                  {formatDate(user.joinDate)}
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-sm" style={{ color: textSecondary }}>
                  {hasActiveFilters
                    ? t('admin.users.noResultsFound', 'No users found matching your filters')
                    : t('admin.users.noUsers', 'No users found')
                  }
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2 flex-wrap gap-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1 || loading}
            className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ border: `1px solid ${paginationBorder}`, color: paginationText }}
          >
            <ChevronLeft size={12} />
            {t('admin.pagination.previous', 'Previous')}
          </button>

          <div className="flex items-center gap-1">
            {renderPaginationItems()}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || loading}
            className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ border: `1px solid ${paginationBorder}`, color: paginationText }}
          >
            {t('admin.pagination.next', 'Next')}
            <ChevronRight size={12} />
          </button>
        </div>
      )}
    </div>
  );
}
